/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { Image, UnstyledButton } from '@/shared/ui';
import { getInitials } from '@/shared/utils/get-initials';
import { cn } from '@/shared/ui/utils/tailwind';

interface UserAvatarButtonProps {
  fullName: string;
  photoUrl?: string;
  onClick: () => void;
  ariaLabel?: string;
  className?: string;
}

/**
 * Circular avatar button showing the user's photo or initials.
 */
export const UserAvatarButton: React.FC<UserAvatarButtonProps> = ({
  fullName,
  photoUrl,
  onClick,
  ariaLabel,
  className,
}) => {
  const initials = getInitials(fullName);

  return (
    <UnstyledButton
      data-testid="user-avatar-button"
      className={cn(
        'w-[32px] h-[32px] rounded-full flex items-center justify-center bg-(--seed-base) text-(--text-inverse) text-body font-(--font-semibold) leading-none cursor-pointer border-2 border-(--border-subtle) overflow-hidden shrink-0 transition-[border-color,box-shadow] duration-150 ease-in-out p-0 hover:border-(--seed-base) focus-visible:border-(--brand-accent) max-md:w-[32px] max-md:h-[32px] max-md:text-small',
        className,
      )}
      onClick={onClick}
      aria-label={ariaLabel || 'User menu'}
    >
      {photoUrl ? (
        <Image src={photoUrl} alt={fullName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        initials
      )}
    </UnstyledButton>
  );
};
